/**
 * SH Updated: Add script to handle variant options
 * Made to Order Collection ID = 483790749925
 * In Stock Collection ID = 483790717157
 *
 * When a user clicks a product from either "made to order" or "in stock" collection, the url will have a "collection_id" parameter
 * 
 * If the "collection_id" parameter is present, then the script will check the "variant_options" data-variant-options on the page
 * 
 * if the "collection_id" = 483790717157, then the script will check the "available" field of the variant_options
 * if the "quantity" is 0, then the script will hide the unavailable options in the variant-selects
 * if the "quantity" is greater than 0, then the script will keep the variant in the variant_options and show the available options in the variant-selects
 *
 * if the "collection_id" = 483790749925, then the script will check the "available" field of the variant_options
 * if the "quantity" is 0, then the script will hide the variant options in the variant_options and show the available options in the variant-selects
 * if the "quantity" is greater than 0, then the script will show the unavailable options in the variant-selects
 * 
 * This script dynamically handles products with single or multiple options.
 */

// Collection IDs
const IN_STOCK_COLLECTION_ID = '483790717157';
const MADE_TO_ORDER_COLLECTION_ID = '483790749925';

// Guard flags to prevent infinite loops
let isValidating = false;
let isCascading = false;
let isHandlingDOMReplacement = false;

/**
 * SH MEDIA GALLERY FILTERING
 *
 * Filters product media gallery based on collection-specific variant availability.
 * Hides images for variants that don't match "In Stock" or "Made to Order" criteria.
 *
 * Dependencies: media-gallery.js, product-info.js, data-variant-options JSON
 */

/**
 * Normalize image URLs for comparison (removes protocol and query params)
 * Example: "//domain.com/image.jpg?v=123&width=1000" → "domain.com/image.jpg"
 */
function normalizeImageUrl(url) {
  if (!url) return '';
  return url.replace(/^https?:/, '').replace(/^\/\//, '').split('?')[0];
}

/**
 * Build Set of image URLs from filtered variants for O(1) lookup
 * @param {Array} filteredVariants - Variants that passed collection filtering
 * @returns {Set<string>} Normalized image URLs
 */
function buildValidImageUrlsSet(filteredVariants) {
  const validUrls = new Set();
  filteredVariants.forEach(variant => {
    if (variant.variant_image) {
      const normalizedUrl = normalizeImageUrl(variant.variant_image);
      validUrls.add(normalizedUrl);
      console.log(`[SH Media Filter] Valid variant: "${variant.variant_name}" → Image: ${normalizedUrl}`);
    }
  });
  console.log(`[SH Media Filter] Total valid image URLs: ${validUrls.size}`, Array.from(validUrls));
  return validUrls;
}

/**
 * Filter media gallery to show only images for valid variants
 * - Hides media items that don't match validImageUrls
 * - Syncs thumbnails
 * - Sets first valid image as active using MediaGallery API
 *
 * @param {Set<string>} validImageUrls - Normalized URLs from buildValidImageUrlsSet()
 */
function filterMediaGallery(validImageUrls) {
  if (!validImageUrls || validImageUrls.size === 0) {
    console.log('[SH Media Filter] No valid image URLs provided, skipping filtering');
    return;
  }

  const mediaGallery = document.querySelector('media-gallery');
  if (!mediaGallery) {
    console.warn('[SH Media Filter] Media gallery element not found');
    return;
  }

  // Get viewer and thumbnails (pattern from media-gallery.js)
  const viewer = mediaGallery.querySelector('[id^="GalleryViewer"]');
  const thumbnails = mediaGallery.querySelector('[id^="GalleryThumbnails"]');
  if (!viewer) {
    console.warn('[SH Media Filter] Gallery viewer not found');
    return;
  }

  const mediaItems = viewer.querySelectorAll('li[data-media-id]');
  console.log(`[SH Media Filter] Processing ${mediaItems.length} media items in gallery`);

  let firstValidMediaId = null;
  let visibleCount = 0;
  let hiddenCount = 0;

  mediaItems.forEach((mediaItem, index) => {
    // Extract and normalize image URL
    const img = mediaItem.querySelector('img');
    const imgSrc = img?.currentSrc || img?.src;
    const normalizedSrc = normalizeImageUrl(imgSrc);

    const isValid = validImageUrls.has(normalizedSrc);

    console.log(`[SH Media Filter] Media ${index + 1}: ${isValid ? '✓ SHOW' : '✗ HIDE'} - ${normalizedSrc}`);

    // Show/hide media item
    if (isValid) {
      mediaItem.style.display = '';
      mediaItem.classList.remove('sh-filtered-out');
      if (!firstValidMediaId) firstValidMediaId = mediaItem.dataset.mediaId;
      visibleCount++;
    } else {
      mediaItem.style.display = 'none';
      mediaItem.classList.add('sh-filtered-out');
      hiddenCount++;
    }

    // Sync corresponding thumbnail
    if (thumbnails) {
      const thumbnail = thumbnails.querySelector(`li[data-target="${mediaItem.dataset.mediaId}"]`);
      if (thumbnail) {
        thumbnail.style.display = isValid ? '' : 'none';
        console.log(`[SH Media Filter] Thumbnail ${index + 1}: ${isValid ? 'SHOW' : 'HIDE'}`);
      }
    }
  });

  console.log(`[SH Media Filter] Gallery filtering complete: ${visibleCount} visible, ${hiddenCount} hidden`);

  // Set first valid media as active (uses MediaGallery API from media-gallery.js)
  if (firstValidMediaId && mediaGallery.setActiveMedia) {
    console.log(`[SH Media Filter] Setting active media: ${firstValidMediaId}`);
    mediaGallery.setActiveMedia(firstValidMediaId, true);
  } else if (!firstValidMediaId) {
    console.warn('[SH Media Filter] No valid media found to set as active');
  }
}

// Function to apply variant filtering
function applyVariantFiltering() {
  // Get collection ID from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const collectionId = urlParams.get('collection_id');

  console.log('[SH Variant Filter] ========================================');
  console.log('[SH Variant Filter] Starting variant filtering on page load');
  console.log('[SH Variant Filter] ========================================');

  // Only proceed if collection_id parameter is present
  if (!collectionId || (collectionId !== IN_STOCK_COLLECTION_ID && collectionId !== MADE_TO_ORDER_COLLECTION_ID)) {
    console.log('[SH Variant Filter] No valid collection_id found, skipping filtering');
    return;
  }
  
  // Get variant options data
  const variantDataScript = document.querySelector('[data-variant-options]');
  if (!variantDataScript) {
    console.warn('SH Variant Options: No variant data found');
    return;
  }
  
  let variantData;
  try {
    variantData = JSON.parse(variantDataScript.textContent);
  } catch (error) {
    console.error('SH Variant Options: Error parsing variant data', error);
    return;
  }
  
  // Get variant selects element
  const variantSelects = document.querySelector('variant-selects');
  if (!variantSelects) {
    console.warn('SH Variant Options: No variant-selects element found');
    return;
  }
  
  // Function to determine if a variant should be included based on quantity
  function shouldIncludeVariant(collectionId, quantity) {
    const hasStock = parseInt(quantity) > 0;
    
    if (collectionId === IN_STOCK_COLLECTION_ID) {
      // In Stock collection: include only variants with quantity > 0
      return hasStock;
    } else if (collectionId === MADE_TO_ORDER_COLLECTION_ID) {
      // Made to Order collection: include only variants with quantity = 0
      return !hasStock;
    }
    return true; // Default: include all variants
  }
  
  // Filter variants based on collection criteria
  const filteredVariants = variantData.variant_options.filter(variant => {
    return shouldIncludeVariant(collectionId, variant.quantity);
  });

  console.log(`[SH Variant Filter] Collection ID: ${collectionId}`);
  console.log(`[SH Variant Filter] Total variants: ${variantData.variant_options.length}, Filtered variants: ${filteredVariants.length}`);
  filteredVariants.forEach(variant => {
    console.log(`[SH Variant Filter] ✓ "${variant.variant_name}" (quantity: ${variant.quantity})`);
  });

  if (filteredVariants.length === 0) {
    console.warn('SH Variant Options: No variants match the filter criteria');
    return;
  }
  
  // Build a map of valid option values per option position
  // This works for products with any number of options
  const validOptionValuesByPosition = {};
  
  filteredVariants.forEach(variant => {
    if (variant.options && Array.isArray(variant.options)) {
      // Parse the variant name to extract individual option values
      // Variant name format: "Value1 / Value2 / Value3"
      const variantNameParts = variant.variant_name.split(' / ').map(part => part.trim());

      variant.options.forEach((option, optionIndex) => {
        if (!validOptionValuesByPosition[optionIndex]) {
          validOptionValuesByPosition[optionIndex] = {
            optionName: option.option_name,
            values: new Set()
          };
        }

        // Add only the option value at this specific position
        if (variantNameParts[optionIndex]) {
          validOptionValuesByPosition[optionIndex].values.add(variantNameParts[optionIndex]);
        }
      });
    }
  });
  
  // Get all select elements (one for each option)
  const selectElements = variantSelects.querySelectorAll('select');

  console.log(`[SH Variant Filter] Processing ${selectElements.length} option dropdowns`);

  selectElements.forEach((selectElement, selectIndex) => {
    const validValues = validOptionValuesByPosition[selectIndex];

    if (!validValues) {
      console.warn(`SH Variant Options: No valid values found for select at index ${selectIndex}`);
      return;
    }

    console.log(`[SH Variant Filter] Dropdown "${validValues.optionName}": ${validValues.values.size} valid values`, Array.from(validValues.values));

    // Process each option in this select
    const optionElements = selectElement.querySelectorAll('option');
    let visibleCount = 0;
    
    optionElements.forEach(optionElement => {
      const optionValue = optionElement.value;

      // Skip empty values (placeholder options)
      if (!optionValue) {
        return;
      }

      // Check if this option value is in the valid set (case-sensitive exact match)
      let isValid = false;
      for (const validValue of validValues.values) {
        if (validValue === optionValue) {
          // Additional checks: ensure option is available and doesn't have unavailable text
          const isAvailable = optionElement.getAttribute('data-sh-option-value-available') !== 'false';
          const hasUnavailableText = optionElement.textContent.includes('- Unavailable');

          if (isAvailable && !hasUnavailableText) {
            isValid = true;
          }
          break;
        }
      }

      if (isValid) {
        optionElement.style.display = 'block';
        optionElement.removeAttribute('disabled');
        visibleCount++;
      } else {
        optionElement.style.display = 'none';
        optionElement.setAttribute('disabled', 'disabled');
      }
    });

    console.log(`[SH Variant Filter] Dropdown "${validValues.optionName}": ${visibleCount} options visible`);

    // Handle the case where the currently selected option is invalid
    const selectedOption = selectElement.querySelector('option:checked');
    const isSelectedInvalid = selectedOption && (
      selectedOption.style.display === 'none' ||
      selectedOption.getAttribute('data-sh-option-value-available') === 'false' ||
      selectedOption.textContent.includes('- Unavailable')
    );

    if (isSelectedInvalid) {
      // Find the first truly available option (not just visible)
      let firstAvailableOption = null;
      for (const option of selectElement.options) {
        if (option.value &&
            option.style.display !== 'none' &&
            !option.disabled &&
            option.getAttribute('data-sh-option-value-available') !== 'false' &&
            !option.textContent.includes('- Unavailable')) {
          firstAvailableOption = option;
          break;
        }
      }

      if (firstAvailableOption) {
        firstAvailableOption.selected = true;
        // Trigger change event to update the product
        selectElement.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  });

  // Apply cascading filtering based on current selections
  applyCascadingFiltering(collectionId, variantData, filteredVariants);

  // Validate and auto-correct selected variant if needed
  validateAndCorrectSelectedVariant(collectionId, variantData, filteredVariants);

  // Filter media gallery based on collection criteria
  const validImageUrls = buildValidImageUrlsSet(filteredVariants);
  filterMediaGallery(validImageUrls);

  console.log('[SH Variant Filter] ========================================');
  console.log('[SH Variant Filter] Filtering complete on page load');
  console.log('[SH Variant Filter] ========================================');
}

// Function to apply cascading filtering based on selected options
function applyCascadingFiltering(collectionId, variantData, filteredVariants) {
  // Check guard flags to prevent infinite loops
  if (isCascading || isValidating) {
    return;
  }

  // Get variant selects element
  const variantSelects = document.querySelector('variant-selects');
  if (!variantSelects) {
    return;
  }

  // Get all select elements
  const selectElements = variantSelects.querySelectorAll('select');
  if (selectElements.length === 0) {
    return;
  }

  // Get current selections from all selects
  const currentSelections = {};
  selectElements.forEach((selectElement, index) => {
    const value = selectElement.value;
    if (value && value.trim() !== '') {
      currentSelections[index] = value.trim();
    }
  });

  // If no selections, skip cascading (show all valid options)
  if (Object.keys(currentSelections).length === 0) {
    return;
  }

  // Set guard flag
  isCascading = true;

  // For each select that has a selection, filter the OTHER selects
  Object.keys(currentSelections).forEach(selectedIndex => {
    const selectedIndexNum = parseInt(selectedIndex);
    const selectedValue = currentSelections[selectedIndex];

    // Find variants that match this selection AND are in filteredVariants
    const matchingVariants = filteredVariants.filter(variant => {
      const variantParts = variant.variant_name.split(' / ').map(part => part.trim());
      // Case-sensitive exact match
      return variantParts[selectedIndexNum] &&
             variantParts[selectedIndexNum] === selectedValue;
    });

    if (matchingVariants.length === 0) {
      return;
    }

    // Extract valid values for OTHER select positions
    const validValuesByPosition = {};
    matchingVariants.forEach(variant => {
      const variantParts = variant.variant_name.split(' / ').map(part => part.trim());
      variantParts.forEach((part, index) => {
        if (index !== selectedIndexNum) { // Skip the selected position
          if (!validValuesByPosition[index]) {
            validValuesByPosition[index] = new Set();
          }
          validValuesByPosition[index].add(part);
        }
      });
    });

    // Apply filtering to OTHER selects
    selectElements.forEach((selectElement, selectIndex) => {
      // Skip the select that triggered the cascading
      if (selectIndex === selectedIndexNum) {
        return;
      }

      const validValues = validValuesByPosition[selectIndex];
      if (!validValues) {
        return;
      }

      // Process each option in this select
      const optionElements = selectElement.querySelectorAll('option');
      let visibleCount = 0;

      optionElements.forEach(optionElement => {
        const optionValue = optionElement.value;

        // Skip empty values (placeholder options)
        if (!optionValue) {
          return;
        }

        // Check if this option value is in the valid set (case-sensitive exact match)
        let isValid = false;
        for (const validValue of validValues) {
          if (validValue === optionValue) {
            // Additional checks: ensure option is available and doesn't have unavailable text
            const isAvailable = optionElement.getAttribute('data-sh-option-value-available') !== 'false';
            const hasUnavailableText = optionElement.textContent.includes('- Unavailable');

            if (isAvailable && !hasUnavailableText) {
              isValid = true;
            }
            break;
          }
        }

        if (isValid) {
          optionElement.style.display = 'block';
          optionElement.removeAttribute('disabled');
          visibleCount++;
        } else {
          optionElement.style.display = 'none';
          optionElement.setAttribute('disabled', 'disabled');
        }
      });

      // Handle the case where the currently selected option is invalid
      const selectedOption = selectElement.querySelector('option:checked');
      const isSelectedInvalid = selectedOption && (
        selectedOption.style.display === 'none' ||
        selectedOption.getAttribute('data-sh-option-value-available') === 'false' ||
        selectedOption.textContent.includes('- Unavailable')
      );

      if (isSelectedInvalid) {
        // Find the first truly available option (not just visible)
        let firstAvailableOption = null;
        for (const option of selectElement.options) {
          if (option.value &&
              option.style.display !== 'none' &&
              !option.disabled &&
              option.getAttribute('data-sh-option-value-available') !== 'false' &&
              !option.textContent.includes('- Unavailable')) {
            firstAvailableOption = option;
            break;
          }
        }

        if (firstAvailableOption) {
          firstAvailableOption.selected = true;
        } else {
          console.warn(`SH Variant Options: No available option found to auto-select for select ${selectIndex}`);
        }
      }
    });
  });

  // Reset guard flag
  isCascading = false;
}

// Function to validate the selected variant and auto-correct if invalid
function validateAndCorrectSelectedVariant(collectionId, variantData, filteredVariants) {
  // Check guard flag to prevent infinite loops
  if (isValidating) {
    return;
  }

  // Get the currently selected variant data from the page
  const selectedVariantScript = document.querySelector('[data-selected-variant]');
  if (!selectedVariantScript) {
    return;
  }
  
  let selectedVariant;
  try {
    selectedVariant = JSON.parse(selectedVariantScript.textContent);
  } catch (error) {
    console.error('SH Variant Options: Error parsing selected variant', error);
    return;
  }
  
  if (!selectedVariant || !selectedVariant.id) {
    return;
  }
  
  // Find the selected variant in our variant data
  const selectedVariantData = variantData.variant_options.find(v => 
    v.variant_id == selectedVariant.id
  );
  
  if (!selectedVariantData) {
    console.warn('SH Variant Options: Selected variant not found in variant data');
    return;
  }
  
  // Check if this variant is valid for the current collection
  const hasStock = parseInt(selectedVariantData.quantity) > 0;
  let isValidForCollection = false;
  
  if (collectionId === IN_STOCK_COLLECTION_ID) {
    isValidForCollection = hasStock;
  } else if (collectionId === MADE_TO_ORDER_COLLECTION_ID) {
    isValidForCollection = !hasStock;
  }
  
  if (isValidForCollection) {
    return;
  }

  // Set guard flag before making changes
  isValidating = true;

  // Selected variant is INVALID - we need to auto-select a valid alternative
  console.warn('SH Variant Options: Selected variant is INVALID for this collection, finding alternative...');
  
  if (filteredVariants.length === 0) {
    console.error('SH Variant Options: No valid variants available to switch to');
    return;
  }
  
  // Get current selections
  const variantSelects = document.querySelector('variant-selects');
  const selectElements = variantSelects.querySelectorAll('select');
  const currentSelections = Array.from(selectElements).map(select => select.value);
  
  // Try to find a variant that matches as many current selections as possible
  let bestMatch = null;
  let bestMatchScore = -1;
  
  filteredVariants.forEach(variant => {
    const variantParts = variant.variant_name.split(' / ').map(p => p.trim());
    let matchScore = 0;
    
    // Count how many options match current selections
    currentSelections.forEach((selection, index) => {
      if (variantParts[index] === selection) {
        matchScore++;
      }
    });
    
    if (matchScore > bestMatchScore) {
      bestMatchScore = matchScore;
      bestMatch = variant;
    }
  });
  
  if (!bestMatch) {
    // If no match found, just use the first valid variant
    bestMatch = filteredVariants[0];
  }
  
  // Parse the best match variant to get the option values to select
  const targetSelections = bestMatch.variant_name.split(' / ').map(p => p.trim());
  
  // Update the select elements to match the valid variant
  let optionChanged = false;
  selectElements.forEach((selectElement, index) => {
    const targetValue = targetSelections[index];
    if (selectElement.value !== targetValue) {
      // Find the option with this value
      const targetOption = Array.from(selectElement.options).find(opt => opt.value === targetValue);
      if (targetOption && !targetOption.disabled) {
        selectElement.value = targetValue;
        optionChanged = true;
      }
    }
  });
  
  // If we changed any options, trigger a change event to update the product
  if (optionChanged) {
    selectElements[0].dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Reset guard flag after a delay to allow the change event to complete
  setTimeout(() => {
    isValidating = false;
  }, 1000);
}

// Run on initial page load
document.addEventListener('DOMContentLoaded', function() {
  applyVariantFiltering();
  
  // Multiple approaches to detect variant changes
  
  // Approach 1: Listen for the variantChange event from product-info.js
  // This fires AFTER product-info.js replaces the DOM with fresh HTML
  if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, function() {
      // Set guard flag to prevent cascading/validation from triggering during DOM replacement handling
      isHandlingDOMReplacement = true;

      // Wait for HTMLUpdateUtility.viewTransition to complete (~150ms)
      setTimeout(() => {

        // Re-apply only the initial filtering (not cascading/validation to avoid loops)
        const urlParams = new URLSearchParams(window.location.search);
        const collectionId = urlParams.get('collection_id');

        if (!collectionId || (collectionId !== IN_STOCK_COLLECTION_ID && collectionId !== MADE_TO_ORDER_COLLECTION_ID)) {
          isHandlingDOMReplacement = false;
          return;
        }

        const variantDataScript = document.querySelector('[data-variant-options]');
        if (!variantDataScript) {
          isHandlingDOMReplacement = false;
          return;
        }

        let variantData;
        try {
          variantData = JSON.parse(variantDataScript.textContent);
        } catch (error) {
          isHandlingDOMReplacement = false;
          return;
        }

        const variantSelects = document.querySelector('variant-selects');
        if (!variantSelects) {
          isHandlingDOMReplacement = false;
          return;
        }

        // Filter variants
        const filteredVariants = variantData.variant_options.filter(variant => {
          const hasStock = parseInt(variant.quantity) > 0;
          if (collectionId === IN_STOCK_COLLECTION_ID) {
            return hasStock;
          } else if (collectionId === MADE_TO_ORDER_COLLECTION_ID) {
            return !hasStock;
          }
          return true;
        });

        if (filteredVariants.length === 0) {
          isHandlingDOMReplacement = false;
          return;
        }

        // Build valid options map
        const validOptionValuesByPosition = {};
        filteredVariants.forEach(variant => {
          if (variant.options && Array.isArray(variant.options)) {
            const variantNameParts = variant.variant_name.split(' / ').map(part => part.trim());
            variant.options.forEach((option, optionIndex) => {
              if (!validOptionValuesByPosition[optionIndex]) {
                validOptionValuesByPosition[optionIndex] = {
                  optionName: option.option_name,
                  values: new Set()
                };
              }
              if (variantNameParts[optionIndex]) {
                validOptionValuesByPosition[optionIndex].values.add(variantNameParts[optionIndex]);
              }
            });
          }
        });

        // Apply filtering to ALL selects in the fresh DOM
        const selectElements = variantSelects.querySelectorAll('select');
        selectElements.forEach((selectElement, selectIndex) => {
          const validValues = validOptionValuesByPosition[selectIndex];
          if (!validValues) return;

          const optionElements = selectElement.querySelectorAll('option');
          optionElements.forEach(optionElement => {
            const optionValue = optionElement.value;
            if (!optionValue) return;

            let isValid = false;
            for (const validValue of validValues.values) {
              if (validValue.toLowerCase() === optionValue.toLowerCase()) {
                const isAvailable = optionElement.getAttribute('data-sh-option-value-available') !== 'false';
                const hasUnavailableText = optionElement.textContent.includes('- Unavailable');
                if (isAvailable && !hasUnavailableText) {
                  isValid = true;
                }
                break;
              }
            }

            if (isValid) {
              optionElement.style.display = 'block';
              optionElement.removeAttribute('disabled');
            } else {
              optionElement.style.display = 'none';
              optionElement.setAttribute('disabled', 'disabled');
            }
          });
        });

        // Now apply cascading filtering based on current selections in the fresh DOM
        applyCascadingFiltering(collectionId, variantData, filteredVariants);

        // Re-filter gallery after DOM update from product-info.js
        const validImageUrls = buildValidImageUrlsSet(filteredVariants);
        filterMediaGallery(validImageUrls);

        // Reset guard flag
        isHandlingDOMReplacement = false;
      }, 150); // Wait for DOM replacement to complete
    });
  }
  
  // Approach 2: Listen directly to select element changes
  setTimeout(() => {
    const selectElements = document.querySelectorAll('variant-selects select');
    selectElements.forEach(selectElement => {
      selectElement.addEventListener('change', function() {
        // Skip if already cascading, validating, or handling DOM replacement to prevent loops
        if (isCascading || isValidating || isHandlingDOMReplacement) {
          return;
        }

        setTimeout(() => {
          applyVariantFiltering();
        }, 100); // Reduced delay for faster cascading response
      });
    });
  }, 1000);
  
  // Approach 3: MutationObserver fallback
  const observer = new MutationObserver(function(mutations) {
    // Skip if we're already handling DOM replacement via variantChange event
    if (isHandlingDOMReplacement) {
      return;
    }

    mutations.forEach(function(mutation) {
      // Check if variant-selects content was updated
      if (mutation.target.closest && mutation.target.closest('variant-selects')) {
        setTimeout(() => {
          applyVariantFiltering();
        }, 200);
      }
    });
  });
  
  // Start observing variant-selects for changes
  const variantSelects = document.querySelector('variant-selects');
  if (variantSelects) {
    observer.observe(variantSelects, {
      childList: true,
      subtree: true,
      attributes: false
    });
  }
});